/**
 * training-page.tsx — Đào tạo nội bộ: cây thư mục SOP bên trái, tài liệu
 * Markdown hiển thị đẹp ở giữa (mục lục bên phải), sửa bằng Markdown có xem
 * trước và lịch sử phiên bản. Dữ liệu từ /training/*.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import {
  ChevronRight, ChevronDown, FolderOpen, Folder, FileText, Search, Pencil, Save, X, History,
  Plus, FolderPlus, Trash2, GraduationCap, Eye, Columns2, BookOpen,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu'
import { Loading, ErrorState, EmptyState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import { MarkdownView, tocFromMarkdown } from '@/components/markdown/markdown-view'
import { NhanXetPanel } from './nhan-xet-panel'
import { neoLai, type KhoiNeo, type KetQuaNeo } from '@/lib/neo-nhan-xet'
import {
  useTrainingTree, useTrainingDoc, useTrainingVersions, useTrainingSearch,
  useSaveTrainingDoc, useDeleteTrainingDoc, useSaveTrainingFolder, useDeleteTrainingFolder,
  fetchTrainingVersion, restoreTrainingVersion,
  type TrainingFolder, type TrainingDocMeta, useNhanXet,
} from '@/hooks/use-training'

const KHOA_MO = 'training.open-folders'

export function TrainingPage() {
  const { docId } = useParams<{ docId: string }>()
  const navigate = useNavigate()
  const treeQ = useTrainingTree()
  const docQ = useTrainingDoc(docId)
  const [search, setSearch] = useState('')
  const searchQ = useTrainingSearch(search)
  const [editing, setEditing] = useState(false)

  // Thư mục đang mở — nhớ trong localStorage cho tiện đọc lại.
  const [open, setOpen] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(KHOA_MO) ?? '[]') as string[]) } catch { return new Set() }
  })
  const toggle = (id: string) => setOpen((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id)
    try { localStorage.setItem(KHOA_MO, JSON.stringify([...n])) } catch { /* bỏ */ }
    return n
  })

  const tree = treeQ.data
  const canEdit = !!tree?.canEdit
  const childrenOf = useMemo(() => {
    const m = new Map<string | null, TrainingFolder[]>()
    for (const f of tree?.folders ?? []) { const k = f.parentId ?? null; if (!m.has(k)) m.set(k, []); m.get(k)!.push(f) }
    return m
  }, [tree])
  const docsOf = useMemo(() => {
    const m = new Map<string | null, TrainingDocMeta[]>()
    for (const d of tree?.docs ?? []) { const k = d.folderId ?? null; if (!m.has(k)) m.set(k, []); m.get(k)!.push(d) }
    return m
  }, [tree])
  const folderById = useMemo(() => new Map((tree?.folders ?? []).map((f) => [f.id, f])), [tree])

  // Mở sẵn nhánh chứa tài liệu đang xem.
  useEffect(() => {
    const d = tree?.docs.find((x) => x.id === docId)
    if (!d) return
    const can: string[] = []
    let cur = d.folderId
    while (cur) { can.push(cur); cur = folderById.get(cur)?.parentId ?? null }
    if (can.some((c) => !open.has(c))) setOpen((s) => new Set([...s, ...can]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId, tree])

  // Chưa chọn gì → mở tài liệu đầu tiên (README gốc).
  useEffect(() => {
    if (!docId && tree?.docs.length) {
      const goc = docsOf.get(null)?.[0] ?? tree.docs[0]
      navigate(`/training/${goc.id}`, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId, tree])

  useEffect(() => { setEditing(false) }, [docId])

  if (treeQ.isLoading) return <Loading label="Đang tải tài liệu đào tạo..." />
  if (treeQ.isError || !tree) return <ErrorState message="Không tải được tài liệu đào tạo." />

  const duongDan = (folderId: string | null): TrainingFolder[] => {
    const out: TrainingFolder[] = []
    let cur = folderId
    while (cur) { const f = folderById.get(cur); if (!f) break; out.unshift(f); cur = f.parentId }
    return out
  }

  const renderFolder = (f: TrainingFolder, depth: number) => {
    const mo = open.has(f.id)
    const cons = childrenOf.get(f.id) ?? []
    const docs = docsOf.get(f.id) ?? []
    return (
      <div key={f.id}>
        <button
          type="button" onClick={() => toggle(f.id)}
          className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm font-medium hover:bg-muted"
          style={{ paddingLeft: 8 + depth * 14 }}
        >
          {mo ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
          {mo ? <FolderOpen className="h-4 w-4 shrink-0 text-primary" /> : <Folder className="h-4 w-4 shrink-0 text-primary/80" />}
          <span className="min-w-0 flex-1 truncate">{f.name}</span>
          <span className="text-[10px] tabular-nums text-muted-foreground">{docs.length + cons.length}</span>
        </button>
        {mo && (
          <div>
            {docs.map((d) => renderDoc(d, depth + 1))}
            {cons.map((c) => renderFolder(c, depth + 1))}
          </div>
        )}
      </div>
    )
  }
  const renderDoc = (d: TrainingDocMeta, depth: number) => (
    <button
      key={d.id} type="button" onClick={() => navigate(`/training/${d.id}`)}
      className={cn('flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted', d.id === docId && 'bg-primary/10 font-semibold text-primary')}
      style={{ paddingLeft: 8 + depth * 14 + 18 }}
      title={d.title}
    >
      <FileText className="h-4 w-4 shrink-0 opacity-70" />
      <span className="min-w-0 flex-1 truncate">{d.title}</span>
      {(tree?.nhanXetMo?.[d.id] ?? 0) > 0 && (
        <span className="shrink-0 rounded-full bg-amber-400 px-1.5 text-[10px] font-semibold leading-4 text-amber-950" title="Nhận xét đang mở">
          {tree?.nhanXetMo?.[d.id]}
        </span>
      )}
    </button>
  )

  const doc = docQ.data

  return (
    <div className="flex h-[calc(100vh-120px)] min-h-[560px] gap-3">
      {/* ══ Cây thư mục ══ */}
      <aside className="flex w-72 shrink-0 flex-col rounded-lg border bg-background">
        <div className="flex items-center gap-2 border-b p-2">
          <GraduationCap className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">Đào tạo nội bộ</span>
          <span className="ml-auto text-[11px] text-muted-foreground">{tree.docs.length} tài liệu</span>
        </div>
        <div className="border-b p-2">
          <div className="relative">
            <Search className="absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
            <Input className="h-8 pl-8 text-sm" placeholder="Tìm trong SOP…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
          {search.trim().length >= 2 ? (
            <div className="space-y-0.5">
              {searchQ.isLoading && <p className="p-2 text-xs text-muted-foreground">Đang tìm…</p>}
              {searchQ.data?.length === 0 && <p className="p-2 text-xs text-muted-foreground">Không thấy "{search}".</p>}
              {searchQ.data?.map((h) => (
                <button key={h.id} type="button" onClick={() => { navigate(`/training/${h.id}`); setSearch('') }}
                  className="block w-full rounded-md px-2 py-1.5 text-left hover:bg-muted">
                  <div className="truncate text-sm font-medium">{h.title}</div>
                  <div className="truncate text-[11px] text-muted-foreground">{duongDan(h.folderId).map((f) => f.name).join(' › ') || 'Gốc'}</div>
                  {h.trich && <div className="mt-0.5 line-clamp-2 text-[11px] text-muted-foreground">…{h.trich}…</div>}
                </button>
              ))}
            </div>
          ) : (
            <>
              {(docsOf.get(null) ?? []).map((d) => renderDoc(d, 0))}
              {(childrenOf.get(null) ?? []).map((f) => renderFolder(f, 0))}
              {tree.docs.length === 0 && tree.folders.length === 0 && (
                <p className="p-3 text-xs text-muted-foreground">Chưa có tài liệu. {canEdit ? 'Bấm "Tài liệu mới" để bắt đầu, hoặc nhập từ repo SOP bằng scripts/nap-sop.ts.' : ''}</p>
              )}
            </>
          )}
        </div>
        {canEdit && (
          <div className="flex gap-1 border-t p-2">
            <TaoMoiMenu folders={tree.folders} currentFolderId={doc?.folderId ?? null} />
          </div>
        )}
      </aside>

      {/* ══ Nội dung ══ */}
      <main className="flex min-w-0 flex-1 flex-col rounded-lg border bg-background">
        {!docId ? (
          <EmptyState icon={BookOpen} title="Chọn một tài liệu" description="Chọn SOP ở cây bên trái để đọc." />
        ) : docQ.isLoading ? (
          <Loading label="Đang tải tài liệu..." />
        ) : docQ.isError || !doc ? (
          <ErrorState message="Không tải được tài liệu." />
        ) : editing ? (
          <DocEditor
            doc={doc}
            onCancel={() => setEditing(false)}
            onSaved={() => setEditing(false)}
          />
        ) : (
          <DocViewer
            doc={doc}
            breadcrumb={duongDan(doc.folderId)}
            canEdit={canEdit}
            onEdit={() => setEditing(true)}
            onDeleted={() => navigate('/training')}
          />
        )}
      </main>
    </div>
  )
}

// ── Xem ──────────────────────────────────────────────────────────────────────

function DocViewer({ doc, breadcrumb, canEdit, onEdit, onDeleted }: {
  doc: NonNullable<ReturnType<typeof useTrainingDoc>['data']>
  breadcrumb: TrainingFolder[]
  canEdit: boolean
  onEdit: () => void
  onDeleted: () => void
}) {
  const toc = useMemo(() => tocFromMarkdown(doc.content), [doc.content])
  const [historyOpen, setHistoryOpen] = useState(false)
  const del = useDeleteTrainingDoc()
  const bodyRef = useRef<HTMLDivElement>(null)
  const [activeId, setActiveId] = useState<string | null>(null)

  // ── Nhận xét theo khối ──
  const nhanXetQ = useNhanXet(doc.id)
  const comments = nhanXetQ.data?.comments ?? []
  const [khoiDanhSach, setKhoiDanhSach] = useState<KhoiNeo[]>([])
  const onKhoiSanSang = useCallback((k: KhoiNeo[]) => setKhoiDanhSach(k), [])
  const [tabPhai, setTabPhai] = useState<'muc_luc' | 'nhan_xet'>('muc_luc')
  const [soanTai, setSoanTai] = useState<KhoiNeo | null>(null)
  const [khoiNoiBat, setKhoiNoiBat] = useState<number | null>(null)
  // Neo lại từng nhận xét gốc vào khối hiện tại (tài liệu có thể đã sửa).
  const neo = useMemo(() => {
    const m = new Map<string, KetQuaNeo>()
    for (const c of comments) if (!c.parentId) m.set(c.id, khoiDanhSach.length ? neoLai(c, khoiDanhSach) : { line: c.line, daDoi: false })
    return m
  }, [comments, khoiDanhSach])
  const soNhanXet = useMemo(() => {
    const m = new Map<number, number>()
    for (const c of comments) {
      if (c.parentId || c.status !== 'open') continue
      const line = neo.get(c.id)?.line
      if (line != null) m.set(line, (m.get(line) ?? 0) + 1)
    }
    return m
  }, [comments, neo])
  const soMo = comments.filter((c) => !c.parentId && c.status === 'open').length
  const cuonToiKhoi = (line: number | null) => {
    if (line == null) return
    setKhoiNoiBat(line)
    bodyRef.current?.querySelector(`[data-dong="${line}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
  const batDauNhanXet = (k: KhoiNeo) => { setSoanTai(k); setKhoiNoiBat(k.line); setTabPhai('nhan_xet') }
  useEffect(() => { setSoanTai(null); setKhoiNoiBat(null) }, [doc.id])

  // Mục lục tự đánh dấu mục đang đọc.
  useEffect(() => {
    const root = bodyRef.current
    if (!root || toc.length === 0) return
    const els = toc.map((t) => root.querySelector<HTMLElement>(`#${CSS.escape(t.id)}`)).filter(Boolean) as HTMLElement[]
    const io = new IntersectionObserver((entries) => {
      const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
      if (vis[0]) setActiveId((vis[0].target as HTMLElement).id)
    }, { root, rootMargin: '0px 0px -70% 0px', threshold: 0 })
    els.forEach((el) => io.observe(el))
    return () => io.disconnect()
  }, [toc, doc.id])

  const cuon = (id: string) => bodyRef.current?.querySelector(`#${CSS.escape(id)}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })

  async function xoa() {
    if (!window.confirm(`Xoá tài liệu "${doc.title}"? Lịch sử phiên bản cũng mất.`)) return
    try { await del.mutateAsync(doc.id); toast.success('Đã xoá tài liệu'); onDeleted() }
    catch (err) { toast.error(apiError(err)) }
  }

  return (
    <>
      <div className="flex items-center gap-2 border-b px-4 py-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[11px] text-muted-foreground">
            {breadcrumb.length ? breadcrumb.map((f) => f.name).join(' › ') : 'Gốc'}
          </div>
          <div className="flex items-center gap-2">
            <h1 className="truncate text-base font-semibold">{doc.title}</h1>
            <Badge variant="outline" className="shrink-0 text-[10px]">v{doc.version}</Badge>
          </div>
        </div>
        <span className="hidden text-[11px] text-muted-foreground md:inline">Cập nhật {dayjs(doc.updatedAt).format('DD/MM/YYYY HH:mm')}</span>
        <Button variant="ghost" size="sm" onClick={() => setHistoryOpen(true)} title="Lịch sử phiên bản"><History className="h-4 w-4" /></Button>
        {canEdit && (
          <>
            <Button size="sm" onClick={onEdit}><Pencil className="h-4 w-4" /> Sửa</Button>
            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => void xoa()} title="Xoá tài liệu"><Trash2 className="h-4 w-4" /></Button>
          </>
        )}
      </div>
      <div className="flex min-h-0 flex-1">
        <div ref={bodyRef} className="min-w-0 flex-1 overflow-y-auto px-6 py-5 pr-12" onClick={() => setKhoiNoiBat(null)}>
          <MarkdownView
            content={doc.content}
            khoi={{ onNhanXet: canEdit ? batDauNhanXet : undefined, soNhanXet, khoiNoiBat, onKhoiSanSang }}
          />
        </div>
        <aside className={cn('hidden w-72 shrink-0 flex-col border-l lg:flex', tabPhai === 'muc_luc' && toc.length <= 1 && soMo === 0 && !soanTai && 'lg:hidden')}>
          <div className="flex gap-1 border-b px-2 pt-2" role="tablist">
            {([['muc_luc', 'Mục lục'], ['nhan_xet', `Nhận xét${soMo ? ` (${soMo})` : ''}`]] as const).map(([k, nhan]) => (
              <button
                key={k} type="button" role="tab" aria-selected={tabPhai === k} onClick={() => setTabPhai(k)}
                className={cn('-mb-px border-b-2 px-2 pb-1.5 text-xs font-medium', tabPhai === k ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground')}
              >
                {nhan}
              </button>
            ))}
          </div>
          {tabPhai === 'muc_luc' ? (
            <nav className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
              {toc.length <= 1 && <p className="text-[11px] text-muted-foreground">Tài liệu chưa có tiêu đề cấp 2/3.</p>}
              {toc.map((t) => (
                <button
                  key={t.id} type="button" onClick={() => cuon(t.id)}
                  className={cn('block w-full truncate rounded px-2 py-1 text-left text-xs hover:bg-muted', t.level === 3 && 'pl-5 text-muted-foreground', activeId === t.id && 'bg-primary/10 font-semibold text-primary')}
                  title={t.text}
                >
                  {t.text}
                </button>
              ))}
            </nav>
          ) : (
            <NhanXetPanel
              docId={doc.id}
              canEdit={canEdit}
              me={nhanXetQ.data?.me ?? ''}
              isAdmin={nhanXetQ.data?.isAdmin ?? false}
              comments={comments}
              neo={neo}
              soanTai={soanTai}
              onDongSoan={() => setSoanTai(null)}
              onChonKhoi={cuonToiKhoi}
              khoiNoiBat={khoiNoiBat}
            />
          )}
        </aside>
      </div>
      {historyOpen && <HistoryDialog docId={doc.id} currentVersion={doc.version} canEdit={canEdit} onClose={() => setHistoryOpen(false)} />}
    </>
  )
}

// ── Sửa ──────────────────────────────────────────────────────────────────────

function DocEditor({ doc, onCancel, onSaved }: {
  doc: NonNullable<ReturnType<typeof useTrainingDoc>['data']>
  onCancel: () => void
  onSaved: () => void
}) {
  const [title, setTitle] = useState(doc.title)
  const [content, setContent] = useState(doc.content)
  const [note, setNote] = useState('')
  const [mode, setMode] = useState<'split' | 'edit' | 'preview'>('split')
  const save = useSaveTrainingDoc()
  const daDoi = title !== doc.title || content !== doc.content

  // Ctrl/Cmd+S để lưu.
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key === 's') { e.preventDefault(); void luu() } }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, content, note])

  async function luu() {
    if (!daDoi) { onSaved(); return }
    try {
      await save.mutateAsync({ id: doc.id, title, content, changeNote: note.trim() || undefined })
      toast.success(`Đã lưu — v${doc.version + 1}`)
      onSaved()
    } catch (err) { toast.error(apiError(err)) }
  }

  return (
    <>
      <div className="flex items-center gap-2 border-b px-4 py-2">
        <Input className="h-8 max-w-md font-semibold" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Tiêu đề" />
        <Input className="h-8 max-w-xs text-xs" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ghi chú thay đổi (tuỳ chọn)" />
        <div className="ml-auto flex items-center gap-1 rounded-md border p-0.5">
          {([['edit', Pencil, 'Soạn'], ['split', Columns2, 'Chia đôi'], ['preview', Eye, 'Xem']] as const).map(([k, Icon, nhan]) => (
            <button key={k} type="button" onClick={() => setMode(k)} title={nhan}
              className={cn('rounded px-2 py-1 text-xs', mode === k ? 'bg-primary text-primary-foreground' : 'hover:bg-muted')}>
              <Icon className="h-3.5 w-3.5" />
            </button>
          ))}
        </div>
        <Button variant="ghost" size="sm" onClick={() => { if (!daDoi || window.confirm('Bỏ thay đổi chưa lưu?')) onCancel() }}><X className="h-4 w-4" /> Huỷ</Button>
        <Button size="sm" onClick={() => void luu()} disabled={save.isPending || !daDoi}><Save className="h-4 w-4" /> {save.isPending ? 'Đang lưu…' : 'Lưu'}</Button>
      </div>
      <div className={cn('grid min-h-0 flex-1', mode === 'split' ? 'grid-cols-2' : 'grid-cols-1')}>
        {mode !== 'preview' && (
          <textarea
            className={cn('h-full w-full resize-none bg-transparent p-4 font-mono text-[13px] leading-relaxed outline-none', mode === 'split' && 'border-r')}
            value={content} onChange={(e) => setContent(e.target.value)} spellCheck={false}
            placeholder="# Tiêu đề&#10;&#10;Nội dung Markdown…"
          />
        )}
        {mode !== 'edit' && (
          <div className="min-w-0 overflow-y-auto px-6 py-5">
            <MarkdownView content={content} />
          </div>
        )}
      </div>
      <p className="border-t px-4 py-1.5 text-[11px] text-muted-foreground">
        {'Markdown: "## Tiêu đề", bảng "| a | b |", sơ đồ ```mermaid, trích dẫn "> ⚠️ …" thành hộp cảnh báo. Cmd/Ctrl+S để lưu. Mỗi lần lưu tạo phiên bản mới.'}
      </p>
    </>
  )
}

// ── Lịch sử ──────────────────────────────────────────────────────────────────

function HistoryDialog({ docId, currentVersion, canEdit, onClose }: { docId: string; currentVersion: number; canEdit: boolean; onClose: () => void }) {
  const q = useTrainingVersions(docId, true)
  const [xem, setXem] = useState<{ version: number; content: string; title: string } | null>(null)
  const save = useSaveTrainingDoc()

  async function moi(version: number) {
    try { const v = await fetchTrainingVersion(docId, version); setXem({ version, content: v.content, title: v.title }) }
    catch (err) { toast.error(apiError(err)) }
  }
  async function khoiPhuc(version: number) {
    if (!window.confirm(`Khôi phục nội dung v${version}? Sẽ tạo phiên bản mới v${currentVersion + 1}.`)) return
    try {
      await restoreTrainingVersion(docId, version)
      await save.mutateAsync({ id: docId }) // chỉ để invalidate cache
      toast.success('Đã khôi phục')
      onClose()
    } catch (err) { toast.error(apiError(err)) }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Lịch sử phiên bản</DialogTitle>
          <DialogDescription>Mỗi lần lưu là một phiên bản. Bấm để xem lại, khôi phục nếu cần.</DialogDescription>
        </DialogHeader>
        <div className="grid max-h-[60vh] grid-cols-[220px_1fr] gap-3">
          <div className="overflow-y-auto rounded-md border p-1">
            {q.isLoading && <p className="p-2 text-xs text-muted-foreground">Đang tải…</p>}
            {q.data?.map((v) => (
              <button key={v.id} type="button" onClick={() => void moi(v.version)}
                className={cn('block w-full rounded px-2 py-1.5 text-left hover:bg-muted', xem?.version === v.version && 'bg-primary/10')}>
                <div className="flex items-center gap-1 text-sm font-medium">v{v.version} {v.version === currentVersion && <Badge variant="secondary" className="text-[10px]">hiện tại</Badge>}</div>
                <div className="text-[11px] text-muted-foreground">{dayjs(v.createdAt).format('DD/MM HH:mm')}{v.changeNote ? ` · ${v.changeNote}` : ''}</div>
              </button>
            ))}
          </div>
          <div className="overflow-y-auto rounded-md border p-4">
            {xem ? <MarkdownView content={xem.content} /> : <p className="text-xs text-muted-foreground">Chọn một phiên bản để xem.</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Đóng</Button>
          {canEdit && xem && xem.version !== currentVersion && (
            <Button onClick={() => void khoiPhuc(xem.version)}>Khôi phục v{xem.version}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Tạo mới ──────────────────────────────────────────────────────────────────

function TaoMoiMenu({ folders, currentFolderId }: { folders: TrainingFolder[]; currentFolderId: string | null }) {
  const navigate = useNavigate()
  const saveDoc = useSaveTrainingDoc()
  const saveFolder = useSaveTrainingFolder()
  const delFolder = useDeleteTrainingFolder()
  const [folderDialog, setFolderDialog] = useState<null | { parentId: string | null }>(null)
  const [name, setName] = useState('')
  const tenThuMuc = (id: string | null) => (id ? folders.find((f) => f.id === id)?.name ?? '…' : 'Gốc')

  async function taoTaiLieu() {
    try {
      const d = await saveDoc.mutateAsync({ title: 'Tài liệu mới', content: '# Tài liệu mới\n\n## 1. Mục đích\n\n## 2. Các bước\n\n| # | Bước | Người | Kết quả |\n|---|---|---|---|\n| 1 |  |  |  |\n', folderId: currentFolderId })
      navigate(`/training/${d.id}`)
      toast.success(`Đã tạo trong "${tenThuMuc(currentFolderId)}" — bấm Sửa để soạn`)
    } catch (err) { toast.error(apiError(err)) }
  }
  async function taoThuMuc() {
    if (!name.trim()) return
    try { await saveFolder.mutateAsync({ name: name.trim(), parentId: folderDialog?.parentId ?? null }); toast.success('Đã tạo thư mục'); setFolderDialog(null); setName('') }
    catch (err) { toast.error(apiError(err)) }
  }
  async function xoaThuMuc() {
    if (!currentFolderId) return
    if (!window.confirm(`Xoá thư mục "${tenThuMuc(currentFolderId)}"? Tài liệu bên trong chuyển về gốc, thư mục con bị xoá.`)) return
    try { await delFolder.mutateAsync(currentFolderId); toast.success('Đã xoá thư mục'); navigate('/training') }
    catch (err) { toast.error(apiError(err)) }
  }

  return (
    <>
      <Button size="sm" variant="outline" className="flex-1" onClick={() => void taoTaiLieu()} disabled={saveDoc.isPending} title={`Tạo trong "${tenThuMuc(currentFolderId)}"`}>
        <Plus className="h-4 w-4" /> Tài liệu mới
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild><Button size="sm" variant="outline" title="Thư mục"><FolderPlus className="h-4 w-4" /></Button></DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setFolderDialog({ parentId: null })}>Thư mục mới ở gốc</DropdownMenuItem>
          {currentFolderId && <DropdownMenuItem onSelect={() => setFolderDialog({ parentId: currentFolderId })}>Thư mục con trong "{tenThuMuc(currentFolderId)}"</DropdownMenuItem>}
          {currentFolderId && <DropdownMenuItem className="text-destructive" onSelect={() => void xoaThuMuc()}>Xoá thư mục "{tenThuMuc(currentFolderId)}"</DropdownMenuItem>}
        </DropdownMenuContent>
      </DropdownMenu>
      {folderDialog && (
        <Dialog open onOpenChange={(o) => !o && setFolderDialog(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Thư mục mới</DialogTitle><DialogDescription>Trong: {tenThuMuc(folderDialog.parentId)}</DialogDescription></DialogHeader>
            <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Tên thư mục" onKeyDown={(e) => { if (e.key === 'Enter') void taoThuMuc() }} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setFolderDialog(null)}>Huỷ</Button>
              <Button onClick={() => void taoThuMuc()} disabled={!name.trim() || saveFolder.isPending}>Tạo</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}
